
/**
 * Utilities for formatting and data conversion
 */

// Helper function to convert time format for English display
export const convertTimeFormat = (timeString: string): string => {
  return timeString
    .replace(/(\d{1,2}):(\d{2})/g, (match, hours, minutes) => {
      const hour = parseInt(hours, 10);
      const period = hour >= 12 ? 'pm' : 'am';
      const formattedHour = hour % 12 || 12;
      return `${formattedHour}${minutes !== '00' ? `:${minutes}` : ''}${period}`;
    })
    .replace(/Uhr/g, '')
    .trim();
};

// Function to convert working hours format
export const convertWorkingHoursToEnglish = (workingHours: string | null): string => {
  if (!workingHours) return "";
  
  try {
    // Replace day abbreviations
    return workingHours
      .replace(/Mo/g, "Mon")
      .replace(/Di/g, "Tue")
      .replace(/Mi/g, "Wed")
      .replace(/Do/g, "Thu")
      .replace(/Fr/g, "Fri")
      .replace(/Sa/g, "Sat")
      .replace(/So/g, "Sun")
      .replace(/Uhr/g, "")
      .replace(/\d{1,2}:\d{2}/g, match => {
        const [hours, minutes] = match.split(':').map(Number);
        const period = hours >= 12 ? 'pm' : 'am';
        const formattedHours = hours % 12 || 12;
        return `${formattedHours}${minutes ? `:${minutes}` : ''}${period}`;
      });
  } catch (e) {
    console.error("Error converting working hours to English:", e);
    return workingHours || "";
  }
};

// Function to format working hours properly without curly braces or quotes
export const formatWorkingHours = (workingHours: string | null): { de: string; en: string } => {
  if (!workingHours) {
    return {
      de: "Mo-Fr: 9:00-18:00, Sa: 10:00-14:00",
      en: "Mon-Fri: 9am-6pm, Sat: 10am-2pm"
    };
  }
  
  try {
    // Check if the working hours are in JSON format
    if (workingHours.includes('{') && workingHours.includes('}')) {
      // Replace single quotes with double quotes for proper JSON parsing
      const cleanedJSON = workingHours.replace(/'/g, '"');
      const parsedHours = JSON.parse(cleanedJSON);
      
      // Format the hours in the desired format without quotes or braces
      let germanHours = "";
      let englishHours = "";
      
      // Define the day mappings
      const daysDE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
      const daysEN = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
      const germanDays = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];
      
      // Process each day in the JSON
      for (const [day, hours] of Object.entries(parsedHours)) {
        // Skip if hours are empty
        if (!hours) continue;
        
        // Find the correct day abbreviation
        let dayIndexDE = -1;
        for (let i = 0; i < germanDays.length; i++) {
          if (day.includes(germanDays[i])) {
            dayIndexDE = i;
            break;
          }
        }
        
        if (dayIndexDE >= 0) {
          germanHours += `${daysDE[dayIndexDE]}: ${hours}\n`;
          englishHours += `${daysEN[dayIndexDE]}: ${convertTimeFormat(hours as string)}\n`;
        }
      }
      
      // Remove trailing newline
      germanHours = germanHours.trim();
      englishHours = englishHours.trim();
      
      if (germanHours) {
        return {
          de: germanHours,
          en: englishHours
        };
      }
    }
    
    // If it's not valid JSON or empty, try to parse as text
    // Simple text formatting for non-JSON working hours
    const deHours = workingHours.replace(/"/g, '').replace(/[{}]/g, '');
    const enHours = convertWorkingHoursToEnglish(deHours);
    
    return {
      de: deHours,
      en: enHours
    };
  } catch (e) {
    console.error("Error parsing working hours:", e);
    return {
      de: workingHours?.replace(/"/g, '').replace(/[{}]/g, '') || "Mo-Fr: 9:00-18:00, Sa: 10:00-14:00",
      en: convertWorkingHoursToEnglish(workingHours) || "Mon-Fri: 9am-6pm, Sat: 10am-2pm"
    };
  }
};

// Placeholder images for schools without photos
const placeholderImages = [
  "https://images.unsplash.com/photo-1485827404703-89b55fcc595e",
  "https://images.unsplash.com/photo-1487058792275-0ad4aaf24ca7",
  "https://images.unsplash.com/photo-1483058712412-4245e9b90334",
  "https://images.unsplash.com/photo-1439337153520-7082a56a81f4"
];

// Function to get a random placeholder image
export const getRandomPlaceholderImage = (): string => {
  const randomIndex = Math.floor(Math.random() * placeholderImages.length);
  return placeholderImages[randomIndex];
};
